import * as Roles from 'src/constants/roles'

const MASKED_PHONE_NUMBER_PATTERN = /^XXX-XXX-\d{4}$/
const PHONE_NUMBER_IN_TEXT_PATTERN = /\+?\(?\d[\d\s().-]*\d/g

/**
 * Mask a phone number to its last 4 digits: '+15551234567' -> 'XXX-XXX-4567'
 * Anything with fewer than 7 digits comes back unchanged: empty values, anonymous callers,
 * short codes, and a number that is already masked. Same output as api-core's VoipHelper.
 * @param {string|null} phoneNumber
 * @returns {string|null}
 */
export const maskPhoneNumberToLastFour = (phoneNumber) => {
  // an email communication's lead_number is an address, not a number
  if (!phoneNumber || String(phoneNumber).includes('@')) {
    return phoneNumber
  }

  const digits = String(phoneNumber)
    .replace(/(ext\.?|x|#|,|;).*$/i, '')
    .replace(/\D/g, '')

  if (digits.length < 7) {
    return phoneNumber
  }

  return `XXX-XXX-${digits.slice(-4)}`
}

/**
 * Mask every phone number inside a text, keeping the rest: '+15551234567 · Title: Mobile' -> 'XXX-XXX-4567 · Title: Mobile'
 * Runs with fewer than 7 digits are left alone. Only for texts known to hold phone numbers: a date also has 8 digits.
 * @param {string|null} text
 * @returns {string|null}
 */
export const maskPhoneNumbersInText = (text) => {
  if (!text) {
    return text
  }

  return String(text).replace(PHONE_NUMBER_IN_TEXT_PATTERN, maskPhoneNumberToLastFour)
}

/**
 * Whether the value is a number already masked by maskPhoneNumberToLastFour (here or by the API)
 * @param {*} phoneNumber
 * @returns {boolean}
 */
export const isMaskedPhoneNumber = (phoneNumber) => {
  return typeof phoneNumber === 'string' && MASKED_PHONE_NUMBER_PATTERN.test(phoneNumber)
}

/**
 * Whether the user sees contact numbers masked: the company hides them and the user is
 * neither an Admin nor a Supervisor. Same rule as api-core's User::shouldSeeMaskedPhoneNumbers.
 * @param {Object|null} company
 * @param {string[]|null} userRoles
 * @returns {boolean}
 */
export const shouldMaskPhoneNumbers = (company, userRoles) => {
  if (!company?.hide_phone_numbers_from_agents) {
    return false
  }

  return !(userRoles || []).some(role => [Roles.COMPANY_ADMIN, Roles.COMPANY_SUPERVISOR].includes(role))
}

/**
 * A live update to the whole company carries masked numbers, even to a user who sees them in full.
 * Put back each number Talk already holds in full for the same record: the known value is kept when
 * it masks to the incoming one. Agents hold masked numbers, so for them nothing changes.
 * @param {Object|null} known what Talk holds for the record (a row, the open contact)
 * @param {Object|null} incoming the update
 * @returns {Object|null} a copy of incoming with the known numbers, its contact's included
 */
export const keepKnownPhoneNumbers = (known, incoming) => {
  if (!known || !incoming) {
    return incoming
  }

  const kept = { ...incoming }

  for (const field of ['lead_number', 'phone_number']) {
    const knownNumber = known[field]

    if (isMaskedPhoneNumber(incoming[field]) && knownNumber && !isMaskedPhoneNumber(knownNumber) &&
      maskPhoneNumberToLastFour(knownNumber) === incoming[field]) {
      kept[field] = knownNumber
    }
  }

  if (incoming.contact && known.contact) {
    kept.contact = keepKnownPhoneNumbers(known.contact, incoming.contact)
  }

  return kept
}

/**
 * The number an incoming call is from. An agent's leg can show the line's number as caller ID, so the
 * server sends the caller's number, masked for an agent, as the ContactPhoneNumber parameter.
 * @param {Object|null} call the dialer's call, with its custom parameters as an object
 * @returns {string|undefined}
 */
export const incomingCallerNumber = (call) => call?.customParameters?.ContactPhoneNumber || call?.from
